export const gettingStartedCrm={
    "getting-started-with-crm":{
      title:"Getting started with Anydone Automated CRM",
      content:`
      <p>First and foremost, once you created the CRM, you need to understand these CRM General Settings.</p>

      <h3>Quick Overview of CRM General Settings</h3>
      <p>Anydone CRM settings provide a centralized space to manage and streamline your customer support operations. These settings help you configure your workspace, define workflows, and ensure seamless communication across channels. Here’s a breakdown of the key configuration sections available:</p>

      <ol type="1">
        <li>
          <strong>General</strong> – Manage the core settings that define how tickets are created and escalated. 
          <ul>
            <li><strong>Link Ticket Folder:</strong> To create a ticket inside the CRM, you must first link a specific folder. This connection ensures all tickets are organized within a predefined structure. <a href="#">Learn more</a></li>
            <li><strong>Set Handoff Message:</strong> When AI Agents need to escalate a conversation to a human, a custom handoff message is triggered. This setting helps maintain a smooth transition between automation and human support.</li>
          </ul>
        </li>
        <li>
          <strong>Roles</strong> – Default user roles for human agents are already preconfigured, but you can customize them based on your support team’s hierarchy. Create, edit, or remove roles to align with your organization’s needs. <a href="#">Learn more</a>
        </li>
        <li>
          <strong>Agents</strong> – Manage your human customer support agents here. You can assign roles, update agent status, remove users, or reassign responsibilities to ensure optimal team performance. <a href="#">Learn more</a>
        </li>
        <li>
          <strong>Source</strong> – Add or update the channels through which customers can reach you. Anydone CRM supports multichannel, so that you can add and connect the platforms that you and your customer prefer to communicate. <a href="#">Learn more</a>
        </li>
        <li>
          <strong>Labels</strong> – Anydone’s AI automatically tags incoming inquiries with smart, contextual labels. This section allows you to manage labels manually – create, delete, or update them — to keep your CRM clean, searchable, and tailored to your support workflows. <a href="#">Learn more</a>
        </li>
        <li>
          <strong>Fields</strong> – Capture key details from each person who contacts you. While Anydone provides essential default fields, this section lets you create additional fields to collect more contextual information or customize your data capture process. <a href="#">Learn more</a>
        </li>
        <li>
          <strong>Hooks</strong> – Automate data flow and interaction triggers between your CRM and external systems.
          <ul>
            <li><strong>Incoming Hook:</strong>  When someone messages you, this hook lets you view and record specific user attributes in real time, allowing for more personalized interactions.</li>
            <li><strong>Outgoing Hook:</strong>  Automatically send a tailored response when a message is received. This feature helps you maintain engagement and set expectations without manual effort.</li>
            <li><strong>Profile Create Hook:</strong>  Instantly capture, save, and generate a new user profile the moment someone initiates a conversation, ensuring you never miss important contact data.</li>
          </ul>
        </li>
      </ol>

      `
    },
    "managing-crm": {
        title: "Managing the CRM",
        content: `
            <p>
            Anydone <span class="text-blue">CRM is a role-based permission system</span>, ensuring controlled access based on assigned roles. While the CRM dashboard view remains the same for all the workspace members added in CRM, the actions they can perform depend entirely on their assigned roles. 
            </p>
            <h3 class="headingThree">
            For example;
            </h3>
            <ul class="no-listing">
              <li>
              <i class="fa-solid fa-arrow-right-long"></i> <span class-"underline-text">Visibility</span>: All the added members in CRM can view the dashboard with ongoing activities (e.g., All Messages(incoming/outgoing), list of Customers, leads, Contacts, Tickets, Follow-Ups, etc).
              </li>
              <li>
              <i class="fa-solid fa-arrow-right-long"></i> <span class-"underline-text">Access Control</span>: The permissions to interact, create, edit, or delete crm components like tickets, customers details, follow-ups, etc, depend on assigned roles.
              </li>
              </ul>
    
            <p class="text-blue">Checkout to view full guide on CRM roles and permission</p>
            <p>Then, once you’ve created your CRM, it’s already functional and ready for use. However, there are several configurations and settings you can implement to get it up and running effectively and efficiently.</p>

            <h3 class="headingThree">CRM Agents:</h3>
            <p>
            Agents in the CRM are workspace members who can access the CRM folder. Their ability to perform actions or make changes in CRM depends on their assigned permissions at the workspace, project, and folder levels.
            </p>
            <p class="text-blue">Learn how to add Agents in CRM</p>

            <h3 class="headingThree">Create / Edit Roles:</h3>
            <p>
            Anydone CRM comes with several default roles. You have the flexibility to edit, delete, or create new roles that align with your business needs.
            </p>
            <p class="text-blue">Learn how to Create and Edit Roles</p>

            <h3 class="headingThree">Assign Roles to Agents:</h3>
            <p>
            You can customize roles, and assign these roles to the agents. This step clarifies responsibilities  to each team added agents.
            </p>
            <p class="text-blue">Learn how to Create and Edit Roles</p>

            <h3 class="headingThree">Add Source:</h3>
            <p>
            In anydone automated CRM, the source section enables you to define where your customers are reaching out from. With the platform's omnichannel feature, you can add your preferred communication channels as sources.
            </p>
            <p class="text-blue">Learn how to Add Source</p>

            <h3 class="headingThree">Assign Agents to the Conversation:</h3>
            <p>
            Now, you can assign agents with defined roles to specific conversations, ensuring that the right agent is available to address incoming queries.
            </p>
            <p class="text-blue">Learn how to assign agents to the conversation</p>

            <h3 class="headingThree">Link Folder:</h3>
            <p>
            You can only create a ticket in your CRM after linking a project folder. Once linked, you can view the created ticket within the linked project folder.
            </p>
            <p class="text-blue">Learn how to link folder</p>

        `,
      },
      "who-is-an-agent":{
        title:"Who is an Agent in anydone CRM?",
        content:`
        <p>
        Anydone CRM is centrally managed by the project owners, who oversee all aspects of CRM and the members with Owner, Admin, Editor, or Viewer permission will be added as an CRM Agent by default. 
        </p>
        <p>
        You can further assign a role to Agets in CRM. Moreover, simply being an Agent does not grant them the ability to respond to messages directly — Agents must first become a Responder to start interacting.
        </p>
        `
      },
      "how-to-add-crm":{
        title:"How to Add a CRM Agent?",
        content:`
        <p>
        There are three steps that need to be followed to add an Agent to your CRM:
        </p>
        <p>
        First, ensure that the member you want to add as an agent to your CRM is already a workspace member. Then follow these steps:
        </p>
        <ul class="steps-list">
        <li>Log in to your anydone account.</li>
        <li>Click on the Project-like icon.</li>
        <li>Select the Project.</li>
        <li>Choose the Project Permissions option from the top.</li>
        <li>Click on the + icon in the right corner.</li>
        <li>Search for and select the member you want to add.</li>
        <li>Go to the CRM folder and define the permissions.</li>
        <li>Click on the Add button.</li>
        </ul>
        <video-component src="https://edge-a.anydone.com/treeleaf/0be98a25-9f30-4371-b22b-2a8c5648431e.mp4"></video-component>

        <p>
        Secondly, once you have added the member to the CRM, you now need to assign them a role 
        </p>
        <ul class="steps-list">
        <li>Log in to your anydone dashboard.</li>
        <li>Go to your CRM.</li>
        <li>From the top, click on the Settings option.</li>
        <li>Click on Agents .</li>
        <li>Click on the + icon in the left corner.</li>
        <li>Select the role for the Agent.</li>
        <li>Assign the role to the selected member.</li>
        <li>Click on Add.</li>
        </ul>

        <video-component src="https://edge-a.anydone.com/treeleaf/3838552c-3f2b-42ad-b73b-01748b1f0edb.mp4"></video-component>

        <p class="text-blue">
        Learn how to customize agents role
        </p>

        <p>
        Thirdly, once you assigned them a role in your CRM now you need to assign them to a conversation.
        </p>
        <ul class="steps-list">
        <li>Log in to your anydone dashboard.</li>
        <li>Go to your CRM.</li>
        <li>From the CRM dashboard, select the customer to whom you want to assign an agent.</li>
        <li>Click on the "i" button on the left side</li>
        <li>Under Agents , click on Assign Agent .</li>
        <li>Now, select the agent and click on the Assign button.</li>
        </ul>

        <video-component src="https://edge-a.anydone.com/treeleaf/16ede6ff-d2a7-4e80-bf86-11c613a9a7d2.mp4"></video-component>
        <h3 class="headingThree">
        What Does "Be a Responder" Mean in anydone CRM?
        </h3>
        <p>In order to actively interact and collaborate with incoming messages, an Agent must click on the "Be a Responder" button from the CRM text editor. </p>
        <ul class="no-listing">
        <li>
        <i class="fa-solid fa-arrow-right-long"></i> <span class="underline-text">Key points about Be a Responder:</span>
        <ul class="list-left">
            <li>It applies to all CRM agents except those with Viewer roles.</li>
            <li>Regardless of permissions, a user must opt in as a Responder before they can reply to messages.</li>
            <li>This ensures controlled access to customer conversations, preventing unauthorized interactions.</li>
        </ul>
        </li>
        </ul>
        <video-component src="https://edge-a.anydone.com/treeleaf/1b15a33d-d8f2-44b1-b3d1-f0bef7fb166c.mp4"></video-component>
        `
      },
      "crm-roles":{
        title:"What are CRM Roles ?",
        content:`
        <p>
        In anydone CRM, roles define an agent’s responsibilities, such as Customer Support, Billing, Sales, or Technical Support. However, assigning agent a role is not related to permissions in any way. An agent’s permissions in the CRM are determined by their permissions in the project folder and workspace, not by their assigned role.
        </p>

        <h3 class="headingThree">
        How to customize roles in anydone CRM?
        </h3>
        <ul class="steps-list">
            <li>Open your CRM.</li>
            <li>From the top menu, click on Settings.</li>
            <li>Click on Roles. <br> Here, you can view all default roles. To edit or delete a role, click on the three horizontal dots next to it and select the appropriate option.</li>
            <li>Or, to create a new role, click on the + button from the bottom right corner.</li>
            <li>Enter a role name and description.</li>
            <li>Click on the Create button.</li>
        </ul>
        <video-component src="https://edge-a.anydone.com/treeleaf/84cc0a45-fa8a-4497-b93a-1855387c0048.mp4"></video-component>
        `
      },
      "creating-custom-field-in-crm":{
          title:"Creating a Custom Field in Anydone CRM",
          content:`
            <p>Custom fields help you collect specific information from your customers beyond the default fields provided. Whether it's capturing a unique identifier, selecting preferences, or adding extra context, custom fields ensure your CRM reflects your exact workflow.</p>

            <ul class="steps-list">
              <li>Open your CRM.</li>
              <li>Click on <strong>Settings</strong> from the top navigation bar.</li>
              <li>Select <strong>Fields</strong> from the settings menu.</li>
              <li>Click the <strong>+ icon</strong> located in the top-right corner.</li>
              <li>
                Fill in the required details:
                <ul>
                  <li><a href="#">Field Name</a> (e.g., Company Size)</li>
                  <li><a href="#">Field ID</a> (used internally for reference)</li>
                  <li><a href="#">Description</a> (to clarify the field’s purpose)</li>
                </ul>
              </li>
              <li>From the dropdown, choose your preferred <strong>Field Type</strong> (e.g., Text, Dropdown, Number).</li>
              <li>Based on the field type, set additional options such as predefined values or input limits.</li>
              <li>Click <strong>Create</strong>.</li>
            </ul>

          `
      },
      "what-is-source-in-anydone":{
        title:"What is a Source in anydone CRM?",
        content:`
        <p>
        In anydone CRM, the Add Source allows you to Add the channels from where your potential customers, visitors, interested or existing users contact you. Since anydone CRM supports omnichannel, the source can be anything like anydone’s <span class="text-blue">AIchat</span> or you can add <span class="text-blue">anydone CoSpace</span>. You can also add any <span class="text-blue">email</span> service, <span class="text-blue">phone number</span>, or social media platforms like <span class="text-blue">WhatsApp</span>, <span class="text-blue">Viber</span>, <span class="text-blue">Messenger</span>, or other messaging platforms.
        </p>

        <p>So first before adding the source in your CRM, you need to make sure that you already have <span class="text-blue">integrated your preferred channel in anydone.</span> </p>

        <p>Then, once integrated you need to understand these terminologies:</p>

        <h3 class="headingThree">
        How to Add a Source in anydone CRM ?
        </h3>
        <ul class="steps-list">
        <li>
            Open your CRM.
        </li>
         <li>
            Click on the Settings option from the top right 
        </li>
         <li>
            Select Sources
        </li>
        <li>Click on the + button from the right side corner</li>
        <li>Give app a name then select your preferred option </li>
        <li>Click on add</li>
        </ul>
        `
      }
}